'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useMenu } from '@/hooks/useMenu';
import { cn } from '@/lib/cn';
import { Urls } from '@/lib/urls';
import Link from 'next/link';
import { defaultRootMenuItems } from '../utils';
import SideBarMenuItem from './MenuItem';
import { useGetUserBookmarks } from '@/lib/bookmark/hooks/use-get-user-bookmarks';
import { combineItems } from '@/app/dashboard/bookmarks/utils';
import {
  FolderIcon,
  FolderOpenIcon,
  FolderXIcon,
  LinkIcon,
} from 'lucide-react';
import type { Bookmark, Folder, UserBookmarks } from '@/lib/bookmark/types';
import { DEFAULT_BOOKMARK_COLOR } from '@/lib/bookmark/constants';
import { getBookmarkPath } from '@/lib/bookmark/utils/bookmark-utils';

export function SidebarBookmarks() {
  const { data } = useGetUserBookmarks<UserBookmarks>({
    select: (data) => {
      return {
        ...data,
        folders: data.folders.filter((folder) => folder.kind === 'bookmark'),
      };
    },
  });
  const userBookmarks = data;
  const { sidebarSettings, setSidebarSettings } = useMenu();

  if (!userBookmarks) {
    return null;
  }

  if (
    userBookmarks.bookmarks.length === 0 &&
    userBookmarks.folders.length === 0
  ) {
    return (
      <>
        <div className='group mt-4 w-full px-6'>
          <h4 className='flex items-center justify-between text-xs font-normal'>
            Bokmärken
            <Link
              prefetch={false}
              href={Urls.dashboard.bookmarks}
              className={cn(
                'invisible text-xs font-normal text-muted-foreground transition-all group-hover:visible hover:text-foreground'
              )}
            >
              Hantera
            </Link>
          </h4>
        </div>

        <div className='mx-6 mt-1 rounded-md text-xs'>Du saknar bokmärken</div>
      </>
    );
  }

  const expandedFolderIds = sidebarSettings.expandedItems?.bookmarks;

  return (
    <Accordion
      type='multiple'
      className='mt-2 px-4'
      defaultValue={defaultRootMenuItems}
      onValueChange={(values) => {
        setSidebarSettings((prev) => ({
          ...prev,
          expandedItems: {
            ...prev.expandedItems,
            root: values,
          },
        }));
      }}
      value={sidebarSettings.expandedItems?.root}
    >
      <AccordionItem className='border-none' value='bookmarks'>
        <AccordionTrigger className='my-0.5 flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm text-foreground/90 transition-colors hover:bg-muted-foreground/10 hover:no-underline [&>svg]:opacity-0 [&>svg]:transition-all hover:[&>svg]:opacity-100'>
          <div className='group w-full'>
            <h4 className='flex items-center justify-between text-xs font-normal'>
              Bokmärken
              <Link
                prefetch={false}
                href={Urls.dashboard.bookmarks}
                className={cn(
                  'invisible text-xs font-normal text-muted-foreground transition-all group-hover:visible hover:text-foreground'
                )}
              >
                Hantera
              </Link>
            </h4>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <Accordion
            value={expandedFolderIds}
            onValueChange={(values) => {
              setSidebarSettings((prev) => ({
                ...prev,
                expandedItems: {
                  ...prev.expandedItems,
                  bookmarks: values,
                },
              }));
            }}
            type='multiple'
            className='pt-1'
          >
            <BookmarksTree
              folders={userBookmarks.folders}
              bookmarks={userBookmarks.bookmarks}
              level={0}
              expandedFolderIds={expandedFolderIds}
            />
          </Accordion>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

interface BookmarksTreeProps {
  folders: Folder[];
  bookmarks: Bookmark[];
  level: number;
  expandedFolderIds: string[] | undefined;
}

function BookmarksTree(props: BookmarksTreeProps) {
  const { folders, bookmarks, level, expandedFolderIds } = props;
  const items = combineItems(folders, bookmarks);

  return (
    <>
      {items.map((item) =>
        item.type === 'folder' ? (
          <BookmarkFolder
            key={item.client_id}
            folder={item}
            level={level}
            expandedFolderIds={expandedFolderIds}
          />
        ) : (
          <div
            key={item.client_id}
            className={cn('truncate', level > 0 && 'pl-2')}
          >
            <SidebarBookmarkLink bookmark={item} />
          </div>
        )
      )}
    </>
  );
}

interface BookmarkFolderProps {
  folder: Folder;
  level: number;
  expandedFolderIds: string[] | undefined;
}

function BookmarkFolder(props: BookmarkFolderProps) {
  const { folder, level, expandedFolderIds } = props;
  const isOpen = expandedFolderIds?.includes(folder.client_id) === true;

  const childFolders = folder.child_folders ?? [];
  const childBookmarks = folder.child_bookmarks ?? [];
  const children = combineItems(childFolders, childBookmarks);
  const isEmpty = !children.length;

  return (
    <AccordionItem
      key={folder.client_id}
      className={cn('w-full border-none', level > 0 && 'pl-2')}
      value={folder.client_id}
    >
      <AccordionTrigger
        className={cn(
          'my-0.5 flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm text-secondary-foreground transition-colors hover:bg-muted-foreground/10 hover:no-underline disabled:opacity-50'
        )}
        disabled={isEmpty}
      >
        <div className='flex items-center truncate'>
          {isEmpty ? (
            <FolderXIcon
              className='mr-2 size-4'
              color={folder.color ?? DEFAULT_BOOKMARK_COLOR}
            />
          ) : isOpen ? (
            <FolderOpenIcon
              className='mr-2 size-4'
              color={folder.color ?? DEFAULT_BOOKMARK_COLOR}
            />
          ) : (
            <FolderIcon
              className='mr-2 size-4'
              color={folder.color ?? DEFAULT_BOOKMARK_COLOR}
            />
          )}
          <span className='truncate font-normal text-foreground'>
            {folder.name}{' '}
            {isEmpty && <span className='text-xs'>(Tom mapp)</span>}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        {children.map((item) =>
          item.type === 'folder' ? (
            <BookmarkFolder
              key={item.client_id}
              folder={item}
              level={level + 1}
              expandedFolderIds={expandedFolderIds}
            />
          ) : (
            <div key={item.client_id} className={cn('truncate', 'pl-2')}>
              <SidebarBookmarkLink bookmark={item} />
            </div>
          )
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

interface SidebarBookmarkLinkProps {
  bookmark: Bookmark;
}

function SidebarBookmarkLink({ bookmark }: SidebarBookmarkLinkProps) {
  const path = getBookmarkPath(bookmark);

  return (
    <SideBarMenuItem
      noActiveIndication
      route={{
        icon: (
          <LinkIcon
            className='mr-2 size-4 shrink-0'
            color={bookmark.color ?? DEFAULT_BOOKMARK_COLOR}
          />
        ),
        title: bookmark.name,
        path,
      }}
    />
  );
}
